import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, useDataProvider, useTranslate } from "@refinedev/core";
import { aiTaakVoorstel, useAchtergrondTaak } from "./achtergrondAi";
import {
  ActionBar,
  AlertTriangleIcon,
  Button,
  CheckboxField,
  ChevronRightIcon,
  Field,
  FileTextIcon,
  FormCard,
  GearIcon,
  HeartIcon,
  HomeIcon,
  MicrochipIcon,
  Modal,
  PawIcon,
  PencilIcon,
  PinIcon,
  ReadField,
  RotateLeftIcon,
  SectionHeader,
  SelectField,
  SparklesIcon,
  StatusBadge,
  TextareaField,
  ToggleField,
  PlusIcon,
  TrashIcon,
  XIcon,
  ConfirmDialog,
} from "@fawkes/ui";

import type { SettingsContext } from "./tiles/types";
import "./TakenRulesSettings.css";
import { TaakKeuzesSection } from "./TaakKeuzesSection";

// jve-taken (Δ2 Phase 2, Slice B3): the React port of the Jmix `taken-rules` builder (settings "Taken"
// tile / TakenInstellingenView). It drives GET /api/taken-config + POST /api/taken-config/action — the
// same coarse "load full payload + applyAction(JSON)" contract the Jmix postMessage bridge used, so the
// logic ports straight across (the bridge is swapped for fetch). Scope: the template/rule/package/item
// CRUD the api owns, plus the AI accelerator, live preview, and the read-only Meldingen surface (T7 —
// the FALLBACK_NO_RULE audit rows, now that the taken-runtime migration moved TAKEN_AUDIT_LOG to docasoft).

interface Optie {
  id: string;
  label: string;
}
interface Regel {
  id: string;
  conditionField: string;
  conditionValue: string | null;
  dueOffsetDays: number;
  priority: string | null;
  assignmentOverride: string | null;
  sortOrder: number;
}
interface Template {
  id: string;
  name: string;
  category: string | null;
  triggerType: string | null;
  defaultTitle: string;
  defaultTeam: string | null;
  fallbackPolicy: string | null;
  species: string | null;
  active: boolean | null;
  chipMissing: boolean;
  /** Uit baseConditions.speciesIn: meer dan één soort tegelijk. Leeg = alle soorten. */
  diersoorten?: string[];
  recurrenceInterval?: number | null;
  recurrenceUnit?: string | null;
  recurrenceAnchorMode?: string | null;
  /** De wizard die deze regel start; leeg = de regel maakt gewoon een taak aan. */
  wizardDefinitionId?: string | null;
  wizardNaam?: string | null;
  rules: Regel[];
}
interface Item {
  id: string;
  templateId: string;
  templateName: string | null;
  sortOrder: number;
}
interface Pakket {
  id: string;
  name: string;
  active: boolean | null;
  /** Voor welke soort de groep geldt; leeg = elk dier. */
  species?: string | null;
  items: Item[];
}
interface Opties {
  teams: Optie[];
  prioriteiten: Optie[];
  categorieen: Optie[];
  fallbacks: Optie[];
  conditionFields: Optie[];
  redenenOpname: Optie[];
  recurrenceUnits?: Optie[];
  /** Gepubliceerde, actieve dierwizards — waar een regel er een van kan starten. */
  wizards?: Optie[];
}
interface Melding {
  detail: string | null;
  context: string | null;
  createdAt: string | null;
}
interface TakenConfig {
  templates: Template[];
  packages: Pakket[];
  opties: Opties;
  aiBeschikbaar: boolean;
  meldingen: Melding[];
}
interface AiRuleDraft {
  conditionField: string;
  conditionValue: string | null;
  dueOffsetDays: number;
  priority: string | null;
}
interface AiTemplateDraft {
  name: string;
  defaultTitle: string;
  category: string | null;
  defaultTeam: string | null;
  fallbackPolicy: string | null;
  chipMissing: boolean;
  rules: AiRuleDraft[];
}
interface RedenDeadline {
  reden: string;
  offsetDagen: number;
  prioriteit: string;
}
interface WizardDraft {
  titel: string | null;
  diersoorten: string[];
  alleenAlsChipOntbreekt: boolean;
  team: string | null;
  fallback: string | null;
  redenDeadlines: RedenDeadline[];
}
interface WizardDraftSuggestion {
  beschikbaar: boolean;
  draft: WizardDraft | null;
  vragen: string[];
}
interface PreviewSamenvatting {
  titel: string;
  wanneer: string;
  voorWelkeDieren: string;
  voorwaarde: string | null;
  planning: string[];
  toewijzing: string;
  dubbeleTaken: string;
}
interface PreviewTest {
  omschrijving: string;
  taakAangemaakt: boolean;
  wanneer: string | null;
  uitleg: string;
}
interface PreviewResp {
  samenvatting: PreviewSamenvatting;
  test: PreviewTest[];
}

// Closed word-lists, matching the api (TaskRulePreviewService / WizardRuleLogic). The reden labels mirror
// WizardRuleLogic.REDEN_LABELS so the wizard reads in the same Dutch as the deterministic preview.
const SOORTEN = ["Hond", "Kat", "Konijn", "Vogel", "Overig"];
const REDEN_OPTIES = [
  { value: "SURRENDER", label: "Afstandsdier" },
  { value: "STRAY", label: "Zwerfdier" },
  { value: "RETURN", label: "Teruggave eigenaar" },
  { value: "BIRTH", label: "Geboorte in asiel" },
  { value: "EXCHANGE", label: "Uitwisseling" },
];

const EMPTY_OPTIES: Opties = {
  teams: [],
  prioriteiten: [],
  categorieen: [],
  fallbacks: [],
  conditionFields: [],
  redenenOpname: [],
  recurrenceUnits: [],
  wizards: [],
};

// De tegels bovenaan: dezelfde zes soorten als in de opvangoverzichten, plus wat een regel verder nog
// bij naam noemt. Een regel zonder soort geldt voor alles en telt dus onder elke tegel mee — de tegel
// zegt wat er voor zo'n dier afgaat, niet hoeveel regels die soort in hun tekst hebben staan.
const SOORT_TEGELS = ["Kat", "Hond", "Konijn", "Knaagdier", "Overig", "Vogel"];

const MEERVOUD: Record<string, string> = {
  hond: "honden",
  kat: "katten",
  konijn: "konijnen",
  knaagdier: "knaagdieren",
  vogel: "vogels",
  cavia: "cavia's",
  overig: "overige dieren",
};

function opts(list: Optie[], withEmpty?: string) {
  const base = list.map((o) => ({ value: o.id, label: o.label }));
  return withEmpty ? [{ value: "", label: withEmpty }, ...base] : base;
}

function labelOf(list: Optie[], id: string | null): string {
  if (!id) return "—";
  return list.find((o) => o.id === id)?.label ?? id;
}

/**
 * De regel onder een melding: waar het over ging en wanneer.
 *
 * <p>De datum stond hier als "2026-08-17 10:37". Dat leest als een logregel en niet als een dag;
 * "17 augustus 2026, 10:37" plaatst hem meteen — was dat gisteren of in het voorjaar?
 */
function meldingMeta(m: Melding): string {
  return [m.context ?? "", meldingDatum(m.createdAt)].filter(Boolean).join(" · ");
}

function meldingDatum(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    // Onleesbaar tijdstip: liever de ruwe waarde dan niets, dan is hij nog na te trekken.
    return iso.replace("T", " ").slice(0, 16);
  }
  return d.toLocaleString("nl-NL", {
    day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

// The api leaves `redenenOpname` empty (data-driven Jmix lookup, deferred), so fall back to the local
// REDEN_OPTIES surface labels — mirrors the Snelle taakregel wizard and keeps the Geavanceerd rules
// table reading like Jmix ("Reden = Afstandsdier" rather than the raw enum id).
function redenenOf(opties: Opties): Optie[] {
  return opties.redenenOpname.length
    ? opties.redenenOpname
    : REDEN_OPTIES.map((r) => ({ id: r.value, label: r.label }));
}

export function TakenRulesSettings({
  context,
  onBack,
}: {
  context: SettingsContext;
  onBack: () => void;
}) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useCustom<TakenConfig>({
    url: "taken-config",
    method: "get",
  });
  const { mutate } = useCustomMutation();
  const [aiDraft, setAiDraft] = useState<AiTemplateDraft | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  // Een AI-voorstel dat op de achtergrond klaar is en waarvoor iemand "Bekijken" koos: in de editor zetten.
  const aiTaak = useAchtergrondTaak(aiTaakVoorstel);
  useEffect(() => {
    if (aiTaak.status === "klaar" && aiTaak.bekijken) {
      setAiDraft(aiTaak.resultaat as unknown as AiTemplateDraft);
      aiTaakVoorstel.overgenomen();
    }
  }, [aiTaak]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [pakkettenOpen, setPakkettenOpen] = useState(false);
  // De filters. "Actief" staat voor: dat is waar een beheerder naar komt kijken — wat er nú afgaat.
  const [zoek, setZoek] = useState("");
  const [soort, setSoort] = useState("");
  const [type, setType] = useState("");
  const [moment, setMoment] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ACTIEF");
  const [geopend, setGeopend] = useState<string | null>(null);

  const payload = data?.data;
  const opties = payload?.opties ?? EMPTY_OPTIES;

  // The action endpoint re-sends the full payload (Jmix bridge contract); hand it to `after` so callers
  // that need the freshly-created row (e.g. applying AI-suggested rules to a just-saved template) can.
  function apply(values: Record<string, unknown>, after?: (payload?: TakenConfig) => void) {
    mutate(
      { url: "taken-config/action", method: "post", values },
      {
        onSuccess: (resp) =>
          refetch().then(() => after?.((resp?.data as TakenConfig | undefined) ?? undefined)),
      },
    );
  }

  if (isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (isError) return <p className="fawkes-error">Kon de takenconfiguratie niet laden.</p>;

  const templates = payload?.templates ?? [];
  // Regels voor het inboeken die in geen actieve groep zitten: die doen stil niets.
  const losseRegels = templates.filter((tpl) => tpl.active !== false && !isPeriodiek(tpl)
    && !(payload?.packages ?? []).some((g) => g.active !== false && g.items.some((i) => i.templateId === tpl.id))).length;
  const meldingen = payload?.meldingen ?? [];

  /**
   * Filteren heeft pas zin als je de lijst niet meer in één blik overziet. Daaronder is een rij van
   * vijf keuzevelden boven één regel meer bediening dan inhoud.
   */
  const toonFilters = templates.length > 5;

  // Filteren gebeurt hier, op de volle lijst: de api geeft alles in één payload terug (de Jmix-bridge
  // deed dat ook), dus er is niets te winnen met een ronde langs de server per filterklik.
  //
  // Zonder zichtbare filterbalk filtert er ook niets. Dat is geen detail: de status stond standaard
  // op "Actief", dus een uitgezette regel zou verdwijnen terwijl de knop om hem terug te halen niet
  // meer op het scherm staat. Een bediening verbergen mag nooit gegevens verbergen.
  const naStatus = templates.filter((tpl) =>
    !toonFilters || statusFilter === "" ? true
      : statusFilter === "ACTIEF" ? Boolean(tpl.active) : !tpl.active);

  function past(tpl: Template, negeerSoort = false): boolean {
    if (!toonFilters) return negeerSoort || !soort || geldtVoorSoort(tpl, soort);
    if (teamFilter && tpl.defaultTeam !== teamFilter) return false;
    if (type && tpl.category !== type) return false;
    if (moment && momentVan(tpl) !== moment) return false;
    if (!negeerSoort && soort && !geldtVoorSoort(tpl, soort)) return false;
    const q = zoek.trim().toLowerCase();
    if (q && !`${tpl.name} ${tpl.defaultTitle}`.toLowerCase().includes(q)) return false;
    return true;
  }

  // De tegels tellen los van het soortfilter — anders zou de gekozen tegel altijd het hele aantal
  // tonen en de rest nul, en dan zeggen ze niets meer.
  const zonderSoort = naStatus.filter((tpl) => past(tpl, true));
  const zichtbaar = naStatus.filter((tpl) => past(tpl));

  // Naast de vaste zes: elke soort die een regel bij naam noemt, zodat een regel voor "Fret" niet
  // onzichtbaar in de lijst blijft hangen.
  const genoemd = Array.from(new Set(templates.flatMap(soortenVan)))
    .filter((n) => !SOORT_TEGELS.some((v) => v.toLowerCase() === n.toLowerCase()));
  const soortTegels = [...SOORT_TEGELS, ...genoemd];


  const filtersActief = Boolean(zoek || soort || type || moment || teamFilter) || statusFilter !== "ACTIEF";

  function wisFilters() {
    setZoek("");
    setSoort("");
    setType("");
    setMoment("");
    setTeamFilter("");
    setStatusFilter("ACTIEF");
  }

  return (
    <FormCard>
      <div className="taken-regels">
        {/* Kop: waar je bent en waar dit scherm over gaat. */}
        <div className="taken-regels__kop">
          <span className="taken-regels__kop-icoon" aria-hidden="true">
            <GearIcon width={26} height={26} />
          </span>
          <div>
            <SectionHeader>Automatische taken</SectionHeader>
            <p className="fawkes-muted taken-regels__kruimels">
              {`${t("instellingen.title")} › ${context.label} › Taken`}
            </p>
            <p className="fawkes-muted taken-regels__lead">
              Stel automatische taken in voor dieren die in de opvang aanwezig zijn. Bepaal voor welke
              dieren automatisch een taak of wizard moet starten.
            </p>
          </div>
        </div>

        {/* De soorten als tegels: zo kijk je "wat gaat er af voor een kat". Een regel zonder soort
            geldt voor alles en telt daarom onder elke tegel mee. */}
        <div className="taken-regels__soorten" role="group" aria-label="Filter op diersoort">
          <button
            type="button"
            className={`taken-regels__soort${soort === "" ? " taken-regels__soort--actief" : ""}`}
            aria-pressed={soort === ""}
            onClick={() => setSoort("")}
          >
            <span className="taken-regels__soort-icoon" aria-hidden="true">
              <PawIcon width={20} height={20} />
            </span>
            <span className="taken-regels__soort-naam">Alle</span>
            <span className="taken-regels__soort-aantal">{zonderSoort.length}</span>
          </button>
          {soortTegels.map((naam) => {
            const aantal = zonderSoort.filter((tpl) => geldtVoorSoort(tpl, naam)).length;
            return (
              <button
                key={naam}
                type="button"
                className={`taken-regels__soort${soort === naam ? " taken-regels__soort--actief" : ""}`}
                aria-pressed={soort === naam}
                onClick={() => setSoort(soort === naam ? "" : naam)}
              >
                <span className="taken-regels__soort-icoon" aria-hidden="true">
                  <img src={soortIcoon(naam)} alt="" />
                </span>
                <span className="taken-regels__soort-naam">{naam}</span>
                <span className="taken-regels__soort-aantal">{aantal}</span>
              </button>
            );
          })}
        </div>

        {/* De vier tellers stonden hier eerst: actieve regels, periodieke controles, wizards en
            problemen. Bij één regel zijn dat vier vakken die "1, 0, 0, 1" zeggen — dat is geen
            overzicht maar ruis, en "Wizards" en "Periodieke controles" zijn woorden uit de motor en
            niet uit het werk. Wat er staat, staat eronder in de regels zelf, en de problemen hebben
            verderop hun eigen blok waar ze wél uitleg krijgen. */}

        {/* Zoeken en filteren op één rij, met rechts de knop waarvoor je hier komt.

            Vijf filters boven één regel is meer bediening dan inhoud: je ziet de hele lijst al. De
            filters verschijnen daarom pas vanaf een handvol regels; daaronder blijft alleen de knop
            staan waarvoor je hier komt. */}
        <div className={`taken-regels__filters${toonFilters ? "" : " taken-regels__filters--kaal"}`}>
          {toonFilters && (
          <>
          <div className="fawkes-products-search taken-regels__zoek">
            <span className="fawkes-products-search__icon" aria-hidden="true">🔍</span>
            <input
              type="search"
              autoComplete="off"
              className="fawkes-products-search__input"
              placeholder="Zoek op taakregel of taak"
              aria-label="Zoek op taakregel of taak"
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
            />
          </div>

          <label className="taken-regels__veld">
            <span className="taken-regels__veld-label">Type</span>
            <select className="taken-regels__filter" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Alle types</option>
              {opties.categorieen.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </label>

          <label className="taken-regels__veld">
            <span className="taken-regels__veld-label">Moment</span>
            <select className="taken-regels__filter" value={moment} onChange={(e) => setMoment(e.target.value)}>
              <option value="">Alle momenten</option>
              <option value="DIRECT">Direct bij binnenkomst</option>
              <option value="NA">Na binnenkomst</option>
              <option value="PERIODIEK">Periodiek</option>
            </select>
          </label>

          <label className="taken-regels__veld">
            <span className="taken-regels__veld-label">Team</span>
            <select className="taken-regels__filter" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}>
              <option value="">Alle teams</option>
              {opties.teams.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </label>

          <label className="taken-regels__veld">
            <span className="taken-regels__veld-label">Status</span>
            <select className="taken-regels__filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="ACTIEF">Actief</option>
              <option value="INACTIEF">Inactief</option>
              <option value="">Alle</option>
            </select>
          </label>

          <Button variant="secondary" disabled={!filtersActief} onClick={wisFilters}>
            <RotateLeftIcon width={14} height={14} aria-hidden="true" /> Filters wissen
          </Button>
          </>
          )}
          {payload?.aiBeschikbaar && (
            <Button variant="secondary" onClick={() => setAiOpen(true)}>
              <SparklesIcon width={14} height={14} aria-hidden="true" /> AI-voorstel
            </Button>
          )}
          <Button onClick={() => setWizardOpen(true)}>Nieuwe taakregel +</Button>
        </div>

        {/* De regels zelf. Eén rij is één regel: wat hij doet in gewone taal, en eronder de vier
            dingen waar je op controleert. */}
        <div className="taken-regels__lijst">
          {zichtbaar.map((tpl) => (
            <TaakregelRij
              key={tpl.id}
              tpl={tpl}
              opties={opties}
              groepen={payload?.packages ?? []}
              onApply={apply}
              open={geopend === tpl.id}
              onOpen={() => setGeopend(geopend === tpl.id ? null : tpl.id)}
            />
          ))}
          {zichtbaar.length === 0 && (
            <p className="fawkes-muted taken-regels__leeg">
              {templates.length === 0
                ? "Er zijn nog geen taakregels. Maak er een met “Nieuwe taakregel”."
                : "Geen taakregels die aan deze filters voldoen."}
            </p>
          )}
        </div>

        <TaakKeuzesSection teams={opties.teams} prioriteiten={opties.prioriteiten} wizards={opties.wizards ?? []} />

        {/* Takengroepen (Maarten, 2026-10-09: "kan wel nuttig zijn, maar dan moet het eenvoudig werken"). Ingeklapt,
            met in de kop een waarschuwing als er een regel is die daardoor niets doet. */}
        <div className="taken-regels__pakketten">
          <button
            type="button"
            className="taken-regels__pakketten-kop"
            aria-expanded={pakkettenOpen}
            onClick={() => setPakkettenOpen((o) => !o)}
          >
            <ChevronRightIcon
              width={16}
              height={16}
              aria-hidden="true"
              className={pakkettenOpen ? "taken-regels__chevron taken-regels__chevron--open" : "taken-regels__chevron"}
            />
            Takengroepen ({(payload?.packages ?? []).length})
            {losseRegels > 0 && (
              <span className="taken-regel__label taken-regel__label--let-op">
                {losseRegels === 1 ? "1 regel doet niets" : `${losseRegels} regels doen niets`}
              </span>
            )}
          </button>
          {(pakkettenOpen) && (
            <div className="takengroepen">
              <div className="takengroepen__kop">
                <p className="fawkes-muted">
                  Een takengroep is een set automatische taken die samen aan of uit gaan als er een dier binnenkomt,
                  bijvoorbeeld &ldquo;Intake kat&rdquo;: alles wat moet gebeuren als er een kat wordt ingeboekt.
                  Alleen taakregels in een actieve groep maken bij het inboeken een taak. Een nieuwe taakregel komt
                  vanzelf in &ldquo;Automatische intake-taken&rdquo;; periodieke taken hebben geen groep nodig.
                </p>
                <Button onClick={() => apply({ actie: "package.save", name: "Nieuwe groep" })}>
                  <PlusIcon width={14} height={14} aria-hidden="true" /> Nieuwe groep
                </Button>
              </div>
              <div className="takengroepen__lijst">
                {(payload?.packages ?? []).map((pkg) => (
                  <GroepKaart key={pkg.id} pkg={pkg} templates={templates} onApply={apply} />
                ))}
              </div>
              {(payload?.packages ?? []).length === 0 && (
                <p className="fawkes-muted">Nog geen takengroepen. Zonder groep maakt het inboeken geen automatische taken.</p>
              )}
            </div>
          )}
        </div>

        <MeldingenSection meldingen={meldingen} />
      </div>

      <ActionBar>
        <Button variant="secondary" onClick={onBack}>
          {t("common.back")}
        </Button>
      </ActionBar>

      {aiOpen && <AiSuggestModal onClose={() => setAiOpen(false)} />}

      {wizardOpen && (
        <RuleWizardModal
          opties={opties}
          aiBeschikbaar={Boolean(payload?.aiBeschikbaar)}
          onApply={apply}
          onClose={() => setWizardOpen(false)}
        />
      )}

      {aiDraft && (
        <TemplateModal
          prefill={aiDraft}
          opties={opties}
          onApply={apply}
          onClose={() => setAiDraft(null)}
        />
      )}
    </FormCard>
  );
}

/**
 * Meldingen (port of the Jmix `taken-rules` RuleBuilder "Meldingen" tab): read-only surface of the
 * FALLBACK_NO_RULE audit rows — cases where the engine fired a task with no matching deadline rule, so
 * a beheerder sees the gap and can add a rule. Newest first, 25 max (the api mirrors the Jmix query).
 */
function MeldingenSection({ meldingen }: { meldingen: Melding[] }) {
  // Niets binnengekomen waar geen regel op paste: dan hoeft dit blok er ook niet te staan.
  if (meldingen.length === 0) return null;
  return (
    <div className="taken-regels__problemen">
      <h4 className="taken-regels__problemen-kop">Waar geen regel op paste</h4>
      {meldingen.length === 0 ? (
        <p className="fawkes-muted taken-regels__probleem-leeg">
          Er is niets binnengekomen waar geen regel op paste.
        </p>
      ) : (
        <>
        {/* Zonder deze zin is een melding een raadsel: je ziet een waarschuwingsdriehoek met een
            dossiernummer en weet niet of je iets moet doen. */}
        <p className="fawkes-muted taken-regels__probleem-uitleg">
          Deze dieren kwamen binnen zonder dat er een regel op paste, dus er is geen taak gemaakt.
          Gebeurt dat vaker, voeg dan een regel toe die dit geval afdekt.
        </p>
        {meldingen.map((m, i) => (
          <div key={i} className="taken-regels__probleem">
            <span className="taken-regels__probleem-icoon" aria-hidden="true">
              <AlertTriangleIcon width={18} height={18} />
            </span>
            <span className="taken-regels__probleem-tekst">
              <span className="taken-regels__probleem-detail">{m.detail || "—"}</span>
              <span className="taken-regels__probleem-meta">{meldingMeta(m)}</span>
            </span>
          </div>
        ))}
        </>
      )}
    </div>
  );
}


/**
 * Eén taakregel als rij: wat hij doet in één zin, en daaronder de vier dingen waarop je controleert —
 * voor welke dieren, wanneer, wat er gebeurt en wie het krijgt.
 *
 * <p>Bewerken klapt de rij open in plaats van een dialoog te openen: de regels en de deadline-tabel
 * horen bij elkaar, en een dialoog erboven zou je het overzicht ontnemen waar je net op stond te kijken.
 */
function TaakregelRij({
  tpl,
  opties,
  groepen,
  onApply,
  open,
  onOpen,
}: {
  tpl: Template;
  opties: Opties;
  groepen: Pakket[];
  onApply: (values: Record<string, unknown>, after?: (payload?: TakenConfig) => void) => void;
  open: boolean;
  onOpen: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const soorten = soortenVan(tpl);
  const reden = redenVan(tpl, opties);
  // In welke groepen deze regel zit. Een regel bij het inboeken doet alleen iets in een actieve groep; een
  // periodieke taak heeft er geen nodig. Zonder dit zag je niet dat een regel stil niets deed.
  const zijnGroepen = groepen.filter((g) => g.items.some((i) => i.templateId === tpl.id));
  const actieveGroep = zijnGroepen.some((g) => g.active !== false);
  const doetNiets = tpl.active !== false && !isPeriodiek(tpl) && !actieveGroep;
  const [groepKeuze, setGroepKeuze] = useState("");

  function dupliceer() {
    setMenuOpen(false);
    const naam = `${tpl.name} (kopie)`;
    onApply(
      {
        actie: "template.save",
        name: naam,
        defaultTitle: tpl.defaultTitle,
        category: tpl.category ?? "OVERIG",
        defaultTeam: tpl.defaultTeam ?? "",
        species: tpl.species ?? "",
        fallbackPolicy: tpl.fallbackPolicy ?? "NONE",
        chipMissing: tpl.chipMissing,
      },
      // De deadline-regels horen bij het sjabloon; zonder die kopie is de kopie een lege huls. En de kopie komt in
      // dezelfde groepen als het origineel, anders doet hij bij het inboeken niets.
      (payload) => {
        const nieuweRegel = payload?.templates?.find((x) => x.name === naam);
        if (!nieuweRegel) return;
        zijnGroepen.forEach((g) => onApply({ actie: "item.add", packageId: g.id, templateId: nieuweRegel.id }));
        tpl.rules.forEach((r) =>
          onApply({
            actie: "rule.save",
            templateId: nieuweRegel.id,
            conditionField: r.conditionField,
            conditionValue: r.conditionValue ?? "",
            dueOffsetDays: r.dueOffsetDays,
            priority: r.priority ?? "",
            sortOrder: r.sortOrder ?? 100,
          }),
        );
      },
    );
  }

  return (
    <div className={`taken-regel${tpl.active ? "" : " taken-regel--uit"}`}>
      <div className="taken-regel__kop">
        <span className="taken-regel__icoon" aria-hidden="true">{regelIcoon(tpl)}</span>

        <div className="taken-regel__tekst">
          <div className="taken-regel__naam-rij">
            <strong className="taken-regel__naam">{tpl.name}</strong>
            {soorten.length ? (
              soorten.map((naam) => (
                <span key={naam} className="taken-regel__label taken-regel__label--soort">{naam}</span>
              ))
            ) : (
              <span className="taken-regel__label taken-regel__label--soort">Alle diersoorten</span>
            )}
            {startEenWizard(tpl) && <span className="taken-regel__label taken-regel__label--type">Wizard</span>}
            {isPeriodiek(tpl) && <span className="taken-regel__label taken-regel__label--type">Periodiek</span>}
            {tpl.chipMissing && <span className="taken-regel__label taken-regel__label--type">Geen chip</span>}
            {reden && <span className="taken-regel__label taken-regel__label--type">{reden}</span>}
            {zijnGroepen.map((g) => (
              <span key={g.id} className={`taken-regel__label taken-regel__label--groep${g.active === false ? " is-uit" : ""}`}
                title={g.active === false ? "Deze groep staat uit" : "Takengroep"}>
                {g.name}
              </span>
            ))}
            {doetNiets && (
              <span className="taken-regel__label taken-regel__label--let-op" title="Alleen regels in een actieve takengroep maken bij het inboeken een taak.">
                Doet niets bij inboeken
              </span>
            )}
          </div>
          <p className="taken-regel__zin">{regelZin(tpl, opties)}</p>
          {doetNiets && groepen.length > 0 && (
            <div className="taken-regel__groep-zetten">
              <select className="fui-field__input" aria-label={`Takengroep voor ${tpl.name}`} value={groepKeuze}
                onChange={(e) => setGroepKeuze(e.target.value)}>
                <option value="">Kies een takengroep…</option>
                {groepen.filter((g) => g.active !== false).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <Button variant="secondary" disabled={!groepKeuze}
                onClick={() => onApply({ actie: "item.add", packageId: groepKeuze, templateId: tpl.id }, () => setGroepKeuze(""))}>
                Aan groep toevoegen
              </Button>
            </div>
          )}
        </div>

        <div className="taken-regel__acties">
          <ToggleField
            label="Actief"
            checked={Boolean(tpl.active)}
            onChange={(e) => onApply({ actie: "template.toggle", id: tpl.id, active: e.target.checked })}
          />
          <Button variant="secondary" onClick={onOpen}>
            <PencilIcon width={14} height={14} aria-hidden="true" /> {open ? "Sluiten" : "Bewerken"}
          </Button>
          <div className="taken-regel__meer">
            <button
              type="button"
              className="taken-regel__meer-knop"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={`Meer acties voor ${tpl.name}`}
              onClick={() => setMenuOpen((o) => !o)}
            >
              ⋯
            </button>
            {menuOpen && (
              <div className="taken-regel__menu" role="menu">
                <button type="button" role="menuitem" onClick={dupliceer}>Dupliceren</button>
                <button
                  type="button"
                  role="menuitem"
                  className="taken-regel__menu-gevaar"
                  onClick={() => {
                    setMenuOpen(false);
                    onApply({ actie: "template.delete", id: tpl.id });
                  }}
                >
                  Verwijderen
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hier stond een strook met "Geldt voor / Wanneer / Actie / Team / Prioriteit". Die zei in
          vijf kolommen precies wat de zin erboven al zegt — en juist door de herhaling ging je
          zoeken naar het verschil. De zin is nu de enige plek waar het staat, mét team en
          prioriteit erin. */}

      {open && (
        <div className="taken-regel__editor">
          <TemplateForm tpl={tpl} opties={opties} onApply={onApply} onDone={onOpen} />
          <RulesTabel tpl={tpl} opties={opties} onApply={onApply} />
        </div>
      )}
    </div>
  );
}

/**
 * The "✨ AI-voorstel" accelerator (port of the Jmix `taken-rules` Geavanceerd-tab `ai.suggest`): a free-
 * text description is sent to POST /api/taken-config/ai-draft (TaskAiService.suggest), and the returned
 * draft prefills the template editor for review before saving — nothing is persisted by the AI step.
 *
 * Het maken duurt even, dus het loopt als achtergrondtaak (aiTaakVoorstel): "Maak voorstel" start hem en
 * sluit het venster, rechtsonder staat de voortgang, en "Bekijken" zet het voorstel hier in de editor.
 */
function AiSuggestModal({ onClose }: { onClose: () => void }) {
  const t = useTranslate();
  const dataProvider = useDataProvider();
  const lopend = useAchtergrondTaak(aiTaakVoorstel);
  const [beschrijving, setBeschrijving] = useState(lopend.status === "fout" ? lopend.beschrijving : "");
  const bezig = lopend.status === "bezig";

  useEffect(() => {
    aiTaakVoorstel.setDialoogOpen(true);
    return () => aiTaakVoorstel.setDialoogOpen(false);
  }, []);

  function suggest() {
    const tekst = beschrijving.trim();
    // Een gewone functie, geen hook-callback: hij moet ook doorlopen als dit venster (of dit scherm) weg is.
    const provider = dataProvider();
    void aiTaakVoorstel.start(tekst, async (b) => {
      const resp = await provider.custom!({ url: "taken-config/ai-draft", method: "post", payload: { beschrijving: b } });
      return resp.data as unknown as Record<string, unknown>;
    });
    onClose();
  }

  return (
    <Modal
      open
      title="Taak met AI voorstellen"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={bezig || !beschrijving.trim()} onClick={suggest}>
            ✨ Maak voorstel
          </Button>
        </>
      }
    >
      <p className="fawkes-muted">
        Beschrijf in gewone taal waar de taak over gaat. De AI stelt een sjabloon met deadline-regels voor
        die je daarna controleert en opslaat.
      </p>
      <TextareaField
        label="Waar gaat de taak over?"
        value={beschrijving}
        onChange={(e) => setBeschrijving(e.target.value)}
        placeholder="bijv. Controleer het chipnummer bij elk afstandsdier binnen een week na inboeken"
      />
      <p className="fawkes-muted">
        Het maken duurt even. Je kunt het venster sluiten en verder werken: rechtsonder zie je wanneer het
        voorstel klaar is.
      </p>
      {bezig && <p className="fawkes-muted">Er wordt al een voorstel gemaakt; wacht tot dat klaar is.</p>}
      {lopend.status === "fout" && (
        <p className="fawkes-error">
          {lopend.code === 401
            ? "Je sessie is verlopen. Log opnieuw in en probeer het nog eens."
            : "Het vorige AI-voorstel is mislukt. Probeer het opnieuw of vul handmatig in."}
        </p>
      )}
    </Modal>
  );
}

/**
 * The guided "Snelle taakregel" wizard (port of the Jmix `taken-rules` wizard): build an intake-task rule
 * in plain language, see a deterministic live preview (POST /preview — "Ik heb dit begrepen" + dry-run
 * outcomes), and activate it (POST /action {actie:"wizard.activate"} — persists a template + rules in the
 * intake package). An optional AI assist (POST /ai-wizard-draft, gated on aiBeschikbaar) prefills the
 * draft from a free-text description and may surface clarifying questions.
 */
function RuleWizardModal({
  opties,
  aiBeschikbaar,
  onApply,
  onClose,
}: {
  opties: Opties;
  aiBeschikbaar: boolean;
  onApply: (values: Record<string, unknown>, after?: (payload?: TakenConfig) => void) => void;
  onClose: () => void;
}) {
  const t = useTranslate();
  const { mutate } = useCustomMutation();
  const [titel, setTitel] = useState("");
  const [diersoorten, setDiersoorten] = useState<string[]>([]);
  const [chip, setChip] = useState(false);
  const [team, setTeam] = useState("");
  const [fallback, setFallback] = useState("NONE");
  const [deadlines, setDeadlines] = useState<RedenDeadline[]>([]);
  const [beschrijving, setBeschrijving] = useState("");
  const [vragen, setVragen] = useState<string[]>([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiUsed, setAiUsed] = useState(false);
  const [preview, setPreview] = useState<PreviewResp | null>(null);

  const redenOpties = opties.redenenOpname.length ? opties.redenenOpname : REDEN_OPTIES.map((r) => ({ id: r.value, label: r.label }));

  // Advisory warnings (port of the Jmix RuleBuilder `breed`/`geenPlanning`): flag a rule that would
  // hit every animal unconditionally, or one that creates no tasks at all. Advisory only — they don't
  // block activation (the footer button already requires a titel + at least one deadline).
  const breed = diersoorten.length === 0 && !chip && deadlines.some((d) => d.reden);
  const geenPlanning = deadlines.filter((d) => d.reden).length === 0 && fallback === "NONE";

  // Live, deterministic preview — recompute (debounced) whenever the draft changes.
  const draftKey = JSON.stringify({ titel, diersoorten, chip, team, fallback, deadlines });
  useEffect(() => {
    const handle = window.setTimeout(() => {
      mutate(
        {
          url: "taken-config/preview",
          method: "post",
          values: {
            titel,
            diersoorten,
            alleenAlsChipOntbreekt: chip,
            team,
            fallback,
            redenDeadlines: deadlines,
          },
        },
        { onSuccess: (resp) => setPreview(resp.data as unknown as PreviewResp), onError: () => setPreview(null) },
      );
    }, 350);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  function aiHulp() {
    setAiBusy(true);
    mutate(
      { url: "taken-config/ai-wizard-draft", method: "post", values: { beschrijving: beschrijving.trim() } },
      {
        onSuccess: (resp) => {
          setAiBusy(false);
          const result = resp.data as unknown as WizardDraftSuggestion;
          setVragen(result.vragen ?? []);
          if (result.draft) {
            setAiUsed(true);
            setTitel(result.draft.titel ?? "");
            setDiersoorten(result.draft.diersoorten ?? []);
            setChip(result.draft.alleenAlsChipOntbreekt);
            setTeam(result.draft.team ?? "");
            setFallback(result.draft.fallback ?? "NONE");
            setDeadlines(result.draft.redenDeadlines ?? []);
          }
        },
        onError: () => setAiBusy(false),
      },
    );
  }

  function toggleSoort(soort: string, on: boolean) {
    setDiersoorten((prev) => (on ? [...prev, soort] : prev.filter((s) => s !== soort)));
  }

  function addDeadline() {
    setDeadlines((prev) => [...prev, { reden: redenOpties[0]?.id ?? "SURRENDER", offsetDagen: 0, prioriteit: "" }]);
  }
  function updateDeadline(i: number, patch: Partial<RedenDeadline>) {
    setDeadlines((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  }
  function removeDeadline(i: number) {
    setDeadlines((prev) => prev.filter((_, idx) => idx !== i));
  }

  function activeren() {
    onApply(
      {
        actie: "wizard.activate",
        titel,
        diersoorten,
        alleenAlsChipOntbreekt: chip,
        team,
        fallback,
        redenDeadlines: deadlines,
        aangemaaktVia: aiUsed ? "AI" : "HANDMATIG",
      },
      onClose,
    );
  }

  return (
    <Modal
      open
      title="Snelle taakregel"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={!titel.trim() || deadlines.length === 0} onClick={activeren}>
            Activeren
          </Button>
        </>
      }
    >
      <p className="fawkes-muted">
        Stel in gewone taal een automatische taak samen die bij het inboeken van een dier wordt aangemaakt.
        Het voorbeeld rechts laat zien wat er gebeurt — niets wordt opgeslagen tot je op Activeren klikt.
      </p>

      {aiBeschikbaar && (
        <div className="settings-taken__wizard-ai">
          <TextareaField
            label="AI-hulp — beschrijf de taak (optioneel)"
            value={beschrijving}
            onChange={(e) => setBeschrijving(e.target.value)}
            placeholder="bijv. Controleer het chipnummer bij elk afstandsdier binnen een week"
          />
          <Button variant="secondary" disabled={aiBusy || !beschrijving.trim()} onClick={aiHulp}>
            {aiBusy ? "AI denkt na…" : "✨ Vul in met AI"}
          </Button>
          {vragen.length > 0 && (
            <ul className="settings-taken__wizard-vragen">
              {vragen.map((v, i) => (
                <li key={i} className="fawkes-muted">
                  {v}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="settings-taken__wizard-grid">
        <div className="settings-taken__wizard-form">
          <Field label="Wat moet er gebeuren?" value={titel} onChange={(e) => setTitel(e.target.value)} />
          <div>
            <span className="fawkes-muted">Voor welke diersoorten? (leeg = alle)</span>
            <div className="settings-taken__wizard-soorten">
              {SOORTEN.map((s) => (
                <CheckboxField
                  key={s}
                  label={s}
                  checked={diersoorten.includes(s)}
                  onChange={(e) => toggleSoort(s, e.target.checked)}
                />
              ))}
            </div>
          </div>
          <ToggleField
            label="Alleen als het chipnummer ontbreekt"
            checked={chip}
            onChange={(e) => setChip(e.target.checked)}
          />
          <SelectField
            label="Team"
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            options={opts(opties.teams, "— niet toegewezen —")}
          />
          <SelectField
            label="Bij onbekende reden opname"
            value={fallback}
            onChange={(e) => setFallback(e.target.value)}
            options={opts(opties.fallbacks)}
          />

          <SectionHeader>Planning per reden opname</SectionHeader>
          {deadlines.map((d, i) => (
            <div key={i} className="settings-taken__wizard-deadline">
              <SelectField
                label="Reden"
                value={d.reden}
                onChange={(e) => updateDeadline(i, { reden: e.target.value })}
                options={redenOpties.map((o) => ({ value: o.id, label: o.label }))}
              />
              <Field
                label="Dagen"
                type="number"
                value={String(d.offsetDagen)}
                onChange={(e) => updateDeadline(i, { offsetDagen: Number(e.target.value) || 0 })}
              />
              <SelectField
                label="Prioriteit"
                value={d.prioriteit}
                onChange={(e) => updateDeadline(i, { prioriteit: e.target.value })}
                options={opts(opties.prioriteiten, "— erft —")}
              />
              <Button variant="secondary" onClick={() => removeDeadline(i)}>
                {t("common.delete")}
              </Button>
            </div>
          ))}
          <Button variant="secondary" onClick={addDeadline}>
            Reden toevoegen
          </Button>

          {breed && (
            <p className="settings-taken__wizard-warning">
              ⚠ Deze regel geldt voor <strong>alle dieren</strong> zonder extra voorwaarde — hij raakt
              mogelijk veel dieren. Kies eventueel diersoorten of "alleen als chip ontbreekt".
            </p>
          )}
          {geenPlanning && (
            <p className="settings-taken__wizard-warning">
              ⚠ Er is nog geen reden van opname gekozen en geen terugval — zo maakt deze regel nog geen
              taken aan.
            </p>
          )}
        </div>

        <div className="settings-taken__wizard-preview">
          <SectionHeader>Ik heb dit begrepen</SectionHeader>
          {preview ? (
            <>
              <dl className="settings-taken__wizard-summary">
                <dt>Taak</dt>
                <dd>{preview.samenvatting.titel}</dd>
                <dt>Wanneer</dt>
                <dd>{preview.samenvatting.wanneer}</dd>
                <dt>Voor welke dieren</dt>
                <dd>{preview.samenvatting.voorWelkeDieren}</dd>
                {preview.samenvatting.voorwaarde && (
                  <>
                    <dt>Voorwaarde</dt>
                    <dd>{preview.samenvatting.voorwaarde}</dd>
                  </>
                )}
                <dt>Planning</dt>
                <dd>
                  <ul>
                    {preview.samenvatting.planning.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </dd>
                <dt>Toewijzing</dt>
                <dd>{preview.samenvatting.toewijzing}</dd>
                <dt>Dubbele taken</dt>
                <dd>{preview.samenvatting.dubbeleTaken}</dd>
              </dl>
              <SectionHeader>Voorbeelden</SectionHeader>
              <ul className="settings-taken__wizard-tests">
                {preview.test.map((tst, i) => (
                  <li key={i}>
                    <StatusBadge tone={tst.taakAangemaakt ? "success" : "neutral"}>
                      {tst.taakAangemaakt ? `Taak ${tst.wanneer ?? ""}`.trim() : "Geen taak"}
                    </StatusBadge>
                    <span>{tst.omschrijving}</span>
                    <span className="fawkes-muted">{tst.uitleg}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="fawkes-muted">Vul de taak in om een voorbeeld te zien.</p>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** Re-read the edited template from the latest payload (so rule mutations reflect after a refetch). */
// The "✨ AI-voorstel" review modal (NEW templates only). The AI draft prefills the fields; on save the
// template is created and any AI-suggested deadline rules are persisted against the fresh row.
function TemplateModal({
  prefill,
  opties,
  onApply,
  onClose,
}: {
  prefill?: AiTemplateDraft | null;
  opties: Opties;
  onApply: (values: Record<string, unknown>, after?: (payload?: TakenConfig) => void) => void;
  onClose: () => void;
}) {
  const t = useTranslate();
  const [name, setName] = useState(prefill?.name ?? "");
  const [defaultTitle, setDefaultTitle] = useState(prefill?.defaultTitle ?? "");
  const [category, setCategory] = useState(prefill?.category ?? "OVERIG");
  const [defaultTeam, setDefaultTeam] = useState(prefill?.defaultTeam ?? "");
  const [fallbackPolicy, setFallbackPolicy] = useState(prefill?.fallbackPolicy ?? "NONE");
  const [chipMissing, setChipMissing] = useState(Boolean(prefill?.chipMissing));

  function save() {
    onApply(
      { actie: "template.save", name, defaultTitle, category, defaultTeam, fallbackPolicy, chipMissing },
      // Persist the AI-suggested deadline rules against the row that was just created (matched by name
      // in the refreshed payload), then close.
      (payload) => {
        const created = payload?.templates?.find((x) => x.name === name);
        if (created && prefill?.rules?.length) {
          prefill.rules.forEach((r) =>
            onApply({
              actie: "rule.save",
              templateId: created.id,
              conditionField: r.conditionField,
              conditionValue: r.conditionValue ?? "",
              dueOffsetDays: r.dueOffsetDays,
              priority: r.priority ?? "",
              sortOrder: 100,
            }),
          );
        }
        onClose();
      },
    );
  }

  return (
    <Modal
      open
      title="Nieuw sjabloon"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={save}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="fawkes-form-grid">
        <Field label="Naam" value={name} onChange={(e) => setName(e.target.value)} />
        <Field
          label="Standaard taaktitel"
          value={defaultTitle}
          onChange={(e) => setDefaultTitle(e.target.value)}
        />
        <SelectField
          label="Categorie"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          options={opts(opties.categorieen)}
        />
        <SelectField
          label="Standaard team"
          value={defaultTeam}
          onChange={(e) => setDefaultTeam(e.target.value)}
          options={opts(opties.teams, "— geen —")}
        />
        <SelectField
          label="Bij geen passende regel"
          value={fallbackPolicy}
          onChange={(e) => setFallbackPolicy(e.target.value)}
          options={opts(opties.fallbacks)}
        />
        <ToggleField
          label="Alleen als chip ontbreekt"
          checked={chipMissing}
          onChange={(e) => setChipMissing(e.target.checked)}
        />
      </div>
      <p className="fawkes-muted">
        Na opslaan verschijnt het sjabloon in de lijst; deadline-regels beheer je daar inline.
      </p>
    </Modal>
  );
}

function TemplateForm({
  tpl,
  opties,
  onApply,
  onDone,
}: {
  tpl: Template;
  opties: Opties;
  onApply: (values: Record<string, unknown>) => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(tpl.name);
  const [defaultTitle, setDefaultTitle] = useState(tpl.defaultTitle ?? "");
  const [category, setCategory] = useState(tpl.category ?? "OVERIG");
  const [defaultTeam, setDefaultTeam] = useState(tpl.defaultTeam ?? "");
  const [species, setSpecies] = useState(tpl.species ?? "");
  const [fallbackPolicy, setFallbackPolicy] = useState(tpl.fallbackPolicy ?? "NONE");
  const [chipMissing, setChipMissing] = useState(Boolean(tpl.chipMissing));
  const [wizardId, setWizardId] = useState(tpl.wizardDefinitionId ?? "");
  const wizards = opties.wizards ?? [];

  function save() {
    onApply({
      actie: "template.save",
      id: tpl.id,
      name,
      defaultTitle,
      category,
      defaultTeam,
      species,
      fallbackPolicy,
      chipMissing,
      // Leeg = een gewone taak. De sleutel gaat altijd mee, anders kun je een gekozen wizard niet
      // meer weghalen (de api laat wat er niet in staat met rust).
      wizardDefinitionId: wizardId,
    });
    onDone();
  }

  return (
    <div className="settings-taken__template-form">
      <div className="fawkes-form-grid">
        <Field label="Naam" value={name} onChange={(e) => setName(e.target.value)} />
        <Field label="Taaktitel" value={defaultTitle} onChange={(e) => setDefaultTitle(e.target.value)} />
        <SelectField
          label="Categorie"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          options={opts(opties.categorieen)}
        />
        <SelectField
          label="Standaardteam"
          value={defaultTeam}
          onChange={(e) => setDefaultTeam(e.target.value)}
          options={opts(opties.teams, "Geen team")}
        />
        <Field
          label="Diersoort (leeg = alle)"
          value={species}
          onChange={(e) => setSpecies(e.target.value)}
          placeholder="bijv. Hond"
        />
        <SelectField
          label="Fallback"
          value={fallbackPolicy}
          onChange={(e) => setFallbackPolicy(e.target.value)}
          options={opts(opties.fallbacks)}
        />
        {/* De actie: een taak om af te vinken, of een taak die een wizard opent. Zonder gepubliceerde
            dierwizards is er niets te kiezen, en dan staat er waarom. */}
        {wizards.length > 0 ? (
          <SelectField
            label="Actie"
            value={wizardId}
            onChange={(e) => setWizardId(e.target.value)}
            options={[
              { value: "", label: `Taak aanmaken: ${defaultTitle || "de taak hierboven"}` },
              ...wizards.map((w) => ({ value: w.id, label: `Wizard starten: ${w.label}` })),
            ]}
          />
        ) : (
          <ReadField
            label="Actie"
            value="Taak aanmaken (er is nog geen gepubliceerde wizard voor dieren)"
          />
        )}
      </div>
      <ToggleField
        label="Taak alleen aanmaken als het chipnummer ontbreekt"
        checked={chipMissing}
        onChange={(e) => setChipMissing(e.target.checked)}
      />
      <div className="settings-taken__form-actions">
        <Button onClick={save}>Opslaan</Button>
        <Button variant="secondary" onClick={onDone}>
          Annuleren
        </Button>
      </div>
    </div>
  );
}

// ── Wat een regel in gewone taal is ─────────────────────────────────────────────────────────
// Alles hieronder leest uit wat er al staat: de soorten uit baseConditions, de herhaling uit de
// recurrence-velden, het moment uit de deadline-regels. Het scherm verzint niets — het zet neer wat de
// regel-engine straks doet, zodat een beheerder in één regel ziet of dit is wat hij bedoelde.

/** Hetzelfde plaatje als op de soorttegels in de opvangoverzichten; onbekend wordt "overig". */
function soortIcoon(naam: string): string {
  const n = (naam ?? "").toLowerCase();
  if (n.includes("hond")) return "/species/hond.png";
  if (n.includes("kat")) return "/species/kat.png";
  if (n.includes("konijn")) return "/species/konijn.png";
  if (n.includes("vogel")) return "/species/vogel.png";
  if (n.includes("knaag")) return "/species/knaagdier.png";
  return "/species/overig.png";
}

/** De soorten waar een regel voor geldt; leeg = alle. */
function soortenVan(tpl: Template): string[] {
  if (tpl.diersoorten && tpl.diersoorten.length) return tpl.diersoorten;
  return tpl.species ? [tpl.species] : [];
}

function meervoud(soort: string): string {
  return MEERVOUD[soort.toLowerCase()] ?? `dieren van de soort ${soort.toLowerCase()}`;
}

function isPeriodiek(tpl: Template): boolean {
  return Boolean(tpl.recurrenceInterval && tpl.recurrenceUnit);
}

function eenheidTekst(unit: string | null | undefined, aantal: number): string {
  if (unit === "DAYS") return aantal === 1 ? "dag" : "dagen";
  if (unit === "WEEKS") return aantal === 1 ? "week" : "weken";
  if (unit === "MONTHS") return aantal === 1 ? "maand" : "maanden";
  return "";
}

/** De reden opname waar een regel op let, als hij op er één let. */
function redenVan(tpl: Template, opties: Opties): string | null {
  const r = tpl.rules.find((x) => x.conditionField === "SHELTER_INTAKE_OPTION" && x.conditionValue);
  return r ? labelOf(redenenOf(opties), r.conditionValue) : null;
}

type Moment = "DIRECT" | "NA" | "PERIODIEK";

function momentVan(tpl: Template): Moment {
  if (isPeriodiek(tpl)) return "PERIODIEK";
  const offsets = tpl.rules.map((r) => r.dueOffsetDays ?? 0);
  return offsets.length > 0 && offsets.every((d) => d === 0) ? "DIRECT" : "NA";
}


/** Start deze regel een wizard, of maakt hij een gewone taak aan? */
function startEenWizard(tpl: Template): boolean {
  return Boolean(tpl.wizardDefinitionId);
}



/** De prioriteit die de regels meegeven; verschillen ze, dan zegt de rij dat eerlijk. */
function prioriteitTekst(tpl: Template, opties: Opties): string {
  const gezet = Array.from(new Set(tpl.rules.map((r) => r.priority).filter(Boolean))) as string[];
  if (gezet.length === 0) return labelOf(opties.prioriteiten, "NORMAAL");
  if (gezet.length > 1) return "Verschilt";
  return labelOf(opties.prioriteiten, gezet[0]);
}

/**
 * De hele regel als één zin, zoals je hem aan een collega zou uitleggen.
 *
 * <p>Dit was de kern van "ik snap er niks van". De oude versie plakte de wanneer-tekst midden in
 * een lijdende vorm, en bij meer dan één termijn stond er letterlijk: "Voor honden zonder chip met
 * reden opname afstandsdier wordt verschilt per reden opname automatisch de taak … aangemaakt."
 * Dat is geen ingewikkeld scherm maar een kapotte zin — en wie dat leest denkt dat híj het niet
 * snapt.
 *
 * <p>Nu staat het in de volgorde waarin je het zou vertellen: wanneer het gebeurt, voor welk dier,
 * en wat het systeem dan doet. De termijn die per reden verschilt krijgt een eigen zin achteraan in
 * plaats van dat hij de eerste ontwricht. Team en prioriteit horen erbij: dat zijn de twee dingen
 * die iemand nog wil weten en waarvoor je anders naar een tabel eronder moet kijken.
 */
function regelZin(tpl: Template, opties: Opties): string {
  const soorten = soortenVan(tpl);
  const wie = soorten.length ? soorten.map(meervoud).join(" en ") : "alle dieren";
  const reden = redenVan(tpl, opties);
  let doel = wie + (tpl.chipMissing ? " zonder chip" : "");
  const taak = `"${tpl.defaultTitle}"`;
  const enDeWizard = startEenWizard(tpl)
    ? `, die de wizard "${tpl.wizardNaam ?? "?"}" opent`
    : "";
  const staart = teamEnPrioriteitZin(tpl, opties);

  if (isPeriodiek(tpl)) {
    const n = tpl.recurrenceInterval as number;
    return `Zolang ${doel} in de opvang zijn, maakt het systeem iedere ${n} `
      + `${eenheidTekst(tpl.recurrenceUnit, n)} de taak ${taak} aan${enDeWizard}.${staart}`;
  }

  const binnenkomst = reden
    ? `Komt er ${lidwoord(doel)} binnen als ${reden.toLowerCase()}`
    : `Komt er ${lidwoord(doel)} binnen`;

  const offsets = Array.from(new Set(tpl.rules.map((rl) => rl.dueOffsetDays ?? 0)));
  if (offsets.length === 0) {
    return `${binnenkomst}, dan zou hier de taak ${taak} horen te ontstaan — maar er staat nog geen `
      + `termijn ingesteld, dus deze regel doet op dit moment niets.`;
  }
  if (offsets.length > 1) {
    return `${binnenkomst}, dan maakt het systeem de taak ${taak} aan${enDeWizard}. `
      + `Hoeveel dagen erna dat is, hangt af van de reden van opname.${staart}`;
  }
  const wanneer = offsets[0] === 0
    ? "meteen"
    : `${offsets[0]} ${offsets[0] === 1 ? "dag" : "dagen"} later`;
  return `${binnenkomst}, dan maakt het systeem ${wanneer} de taak ${taak} aan${enDeWizard}.${staart}`;
}

/**
 * "de honden zonder chip" → "een hond zonder chip". De zin gaat over één dier dat binnenkomt, niet
 * over de hele groep; het meervoud las als een verzameling die tegelijk arriveert.
 */
function lidwoord(doel: string): string {
  // "alle dieren zonder chip" leest in deze zin als een kudde die tegelijk binnenkomt; het gaat
  // over één dier dat aan de balie staat.
  if (doel.startsWith("alle dieren")) return `een dier${doel.slice("alle dieren".length)}`;
  const enkelvoud = doel
    .replace(/^honden/, "hond")
    .replace(/^katten/, "kat")
    .replace(/^konijnen/, "konijn")
    .replace(/^knaagdieren/, "knaagdier")
    .replace(/^vogels/, "vogel");
  return enkelvoud === doel ? doel : `een ${enkelvoud}`;
}

/** Team en prioriteit als afsluitende zin; laat weg wat niet is ingevuld. */
function teamEnPrioriteitZin(tpl: Template, opties: Opties): string {
  const team = tpl.defaultTeam ? labelOf(opties.teams, tpl.defaultTeam) : null;
  const prio = prioriteitTekst(tpl, opties);
  const heeftPrio = prio && prio !== "—";
  if (team && heeftPrio) return ` De taak gaat naar ${team} en heeft prioriteit ${prio.toLowerCase()}.`;
  if (team) return ` De taak gaat naar ${team}.`;
  if (heeftPrio) return ` De taak heeft prioriteit ${prio.toLowerCase()}.`;
  return "";
}

/** Het plaatje bij de rij, naar de categorie: hetzelfde soort houvast als de tegels bovenaan. */
function regelIcoon(tpl: Template) {
  if (startEenWizard(tpl)) return <SparklesIcon width={22} height={22} />;
  if (tpl.chipMissing) return <MicrochipIcon width={22} height={22} />;
  switch (tpl.category) {
    case "INTAKE":
      return <PinIcon width={22} height={22} />;
    case "MEDISCH":
      return <HeartIcon width={22} height={22} />;
    case "ADMINISTRATIE":
      return <FileTextIcon width={22} height={22} />;
    case "ADOPTIE":
      return <HomeIcon width={22} height={22} />;
    case "VERZORGING":
      return <PawIcon width={22} height={22} />;
    default:
      return <SparklesIcon width={22} height={22} />;
  }
}

/** Geldt deze regel voor dieren van deze soort? Een regel zonder soort geldt voor alle. */
function geldtVoorSoort(tpl: Template, soort: string): boolean {
  const soorten = soortenVan(tpl);
  return soorten.length === 0 || soorten.some((s) => s.toLowerCase() === soort.toLowerCase());
}

function voorwaardeTekst(r: Regel, opties: Opties): string {
  if (r.conditionField === "SHELTER_INTAKE_OPTION")
    return "Reden = " + labelOf(redenenOf(opties), r.conditionValue);
  if (r.conditionField === "SPECIES") return "Diersoort = " + (r.conditionValue || "?");
  if (r.conditionField === "CHIP_MISSING") return "Chip ontbreekt";
  return "Altijd";
}

// Columnar deadline-rules table (Jmix taken-rules RulesTabel): #/Voorwaarde/Deadline/Prioriteit/Team,
// with an inline RuleForm for add (+ Regel) and edit (✎).
function RulesTabel({
  tpl,
  opties,
  onApply,
}: {
  tpl: Template;
  opties: Opties;
  onApply: (values: Record<string, unknown>) => void;
}) {
  const [nieuw, setNieuw] = useState(false);
  const [bewerkId, setBewerkId] = useState<string | null>(null);
  return (
    <div className="settings-taken__rules-block">
      <div className="settings-taken__rules-head">
        <span>Deadline-regels (eerste passende telt)</span>
        <button
          type="button"
          className="settings-taken__mini"
          onClick={() => {
            setNieuw((o) => !o);
            setBewerkId(null);
          }}
        >
          {nieuw ? "Sluiten" : "+ Regel"}
        </button>
      </div>
      {tpl.rules.length === 0 && !nieuw && (
        <div className="settings-taken__rules-empty">
          Geen regels — fallback &quot;{labelOf(opties.fallbacks, tpl.fallbackPolicy)}&quot;.
        </div>
      )}
      {tpl.rules.length > 0 && (
        <table className="settings-taken__rules-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Voorwaarde</th>
              <th>Deadline</th>
              <th>Prioriteit</th>
              <th>Team</th>
              <th aria-label="Acties" />
            </tr>
          </thead>
          <tbody>
            {tpl.rules.map((r) =>
              bewerkId === r.id ? (
                <tr key={r.id}>
                  <td colSpan={6}>
                    <RuleForm
                      tpl={tpl}
                      rule={r}
                      opties={opties}
                      onApply={onApply}
                      onDone={() => setBewerkId(null)}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={r.id}>
                  <td>{r.sortOrder}</td>
                  <td>{voorwaardeTekst(r, opties)}</td>
                  <td>{r.dueOffsetDays === 0 ? "Vandaag" : `+${r.dueOffsetDays} dagen`}</td>
                  <td>{r.priority ? labelOf(opties.prioriteiten, r.priority) : "erft"}</td>
                  <td>{r.assignmentOverride ? labelOf(opties.teams, r.assignmentOverride) : "erft"}</td>
                  <td className="settings-taken__rules-actions">
                    <button
                      type="button"
                      className="settings-taken__mini"
                      title="Regel bewerken"
                      onClick={() => {
                        setBewerkId(r.id);
                        setNieuw(false);
                      }}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="settings-taken__mini settings-taken__mini--muted"
                      title="Regel verwijderen"
                      onClick={() => onApply({ actie: "rule.delete", id: r.id })}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
      {nieuw && <RuleForm tpl={tpl} opties={opties} onApply={onApply} onDone={() => setNieuw(false)} />}
    </div>
  );
}

function RuleForm({
  tpl,
  rule,
  opties,
  onApply,
  onDone,
}: {
  tpl: Template;
  rule?: Regel;
  opties: Opties;
  onApply: (values: Record<string, unknown>) => void;
  onDone: () => void;
}) {
  const [conditionField, setConditionField] = useState(
    rule?.conditionField ?? opties.conditionFields[0]?.id ?? "SHELTER_INTAKE_OPTION",
  );
  const [conditionValue, setConditionValue] = useState(rule?.conditionValue ?? "");
  const [dueOffsetDays, setDueOffsetDays] = useState(String(rule?.dueOffsetDays ?? 0));
  const [priority, setPriority] = useState(rule?.priority ?? "");
  const [assignmentOverride, setAssignmentOverride] = useState(rule?.assignmentOverride ?? "");
  const [sortOrder, setSortOrder] = useState(String(rule?.sortOrder ?? (tpl.rules.length + 1) * 10));
  const heeftWaarde = conditionField === "SHELTER_INTAKE_OPTION" || conditionField === "SPECIES";

  function save() {
    onApply({
      actie: "rule.save",
      ...(rule ? { id: rule.id } : {}),
      templateId: tpl.id,
      conditionField,
      conditionValue: heeftWaarde ? conditionValue : "",
      dueOffsetDays: Number(dueOffsetDays) || 0,
      priority,
      assignmentOverride,
      sortOrder: Number(sortOrder) || 100,
    });
    onDone();
  }

  return (
    <div className="settings-taken__rule-form">
      <div className="fawkes-form-grid">
        <Field
          label="Volgorde"
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
        <SelectField
          label="Conditieveld"
          value={conditionField}
          onChange={(e) => setConditionField(e.target.value)}
          options={opts(opties.conditionFields)}
        />
        {conditionField === "SHELTER_INTAKE_OPTION" ? (
          <SelectField
            label="Waarde"
            value={conditionValue}
            onChange={(e) => setConditionValue(e.target.value)}
            options={opts(redenenOf(opties), "Kies reden…")}
          />
        ) : conditionField === "SPECIES" ? (
          <Field
            label="Waarde"
            value={conditionValue}
            onChange={(e) => setConditionValue(e.target.value)}
            placeholder="bijv. Hond"
          />
        ) : null}
        <Field
          label="Deadline (dagen na inboeken)"
          type="number"
          value={dueOffsetDays}
          onChange={(e) => setDueOffsetDays(e.target.value)}
        />
        <SelectField
          label="Prioriteit"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          options={opts(opties.prioriteiten, "Erf van sjabloon")}
        />
        <SelectField
          label="Team"
          value={assignmentOverride}
          onChange={(e) => setAssignmentOverride(e.target.value)}
          options={opts(opties.teams, "Erf van sjabloon")}
        />
      </div>
      <div className="settings-taken__form-actions">
        <Button onClick={save}>Opslaan</Button>
        <Button variant="secondary" onClick={onDone}>
          Annuleren
        </Button>
      </div>
    </div>
  );
}

/**
 * Eén takengroep: naam en soort meteen te wijzigen, de taakregels erin als chips, en een regel erbij kiezen. Uitzetten
 * en verwijderen vragen eerst of je het zeker weet, met hoeveel taken er dan niet meer komen.
 */
function GroepKaart({
  pkg,
  templates,
  onApply,
}: {
  pkg: Pakket;
  templates: Template[];
  onApply: (values: Record<string, unknown>, after?: () => void) => void;
}) {
  const [naam, setNaam] = useState(pkg.name);
  const [toevoegen, setToevoegen] = useState("");
  const [vraag, setVraag] = useState<"uit" | "weg" | null>(null);
  useEffect(() => setNaam(pkg.name), [pkg.name]);
  // Alleen regels voor het inboeken horen in een groep; periodieke taken lopen er los van.
  const beschikbaar = templates.filter((tpl) => !isPeriodiek(tpl) && !pkg.items.some((i) => i.templateId === tpl.id));
  const bewaar = (patch: { name?: string; species?: string | null }) =>
    onApply({ actie: "package.save", id: pkg.id, name: patch.name ?? pkg.name, species: patch.species === undefined ? pkg.species ?? "" : patch.species ?? "" });
  const aan = pkg.active !== false;
  const voorWie = pkg.species ? meervoud(pkg.species) : "elk dier";

  return (
    <section className={`takengroep${aan ? "" : " is-uit"}`}>
      <div className="takengroep__kop">
        <input className="fui-field__input takengroep__naam" value={naam} maxLength={100} aria-label="Naam van de groep"
          onChange={(e) => setNaam(e.target.value)}
          onBlur={() => { if (naam.trim() && naam.trim() !== pkg.name) bewaar({ name: naam.trim() }); }}
          onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
        <ToggleField label={aan ? "Actief" : "Uit"} checked={aan}
          onChange={(e) => (e.target.checked
            ? onApply({ actie: "package.toggle", id: pkg.id, active: true })
            : pkg.items.length > 0 ? setVraag("uit") : onApply({ actie: "package.toggle", id: pkg.id, active: false }))} />
        <button type="button" className="takengroep__weg" aria-label={`Groep ${pkg.name} verwijderen`} title="Groep verwijderen"
          onClick={() => setVraag("weg")}>
          <TrashIcon width={16} height={16} />
        </button>
      </div>
      <div className="takengroep__voor">
        <label>
          <span>Bij het inboeken van</span>
          <select className="fui-field__input" value={pkg.species ?? ""} aria-label="Voor welke diersoort"
            onChange={(e) => bewaar({ species: e.target.value || null })}>
            <option value="">elk dier</option>
            {SOORTEN.map((s) => <option key={s} value={s}>{meervoud(s)}</option>)}
          </select>
        </label>
        <span className="takengroep__telling">
          {pkg.items.length === 1 ? "1 taak" : `${pkg.items.length} taken`}
        </span>
      </div>
      {pkg.items.length === 0 ? (
        <p className="fawkes-muted takengroep__leeg">Nog geen taakregels in deze groep; zo doet hij niets.</p>
      ) : (
        <ul className="takengroep__regels">
          {pkg.items.map((i) => (
            <li key={i.id}>
              <span>{i.templateName ?? "—"}</span>
              <button type="button" aria-label={`${i.templateName ?? "Regel"} uit de groep halen`} title="Uit de groep halen"
                onClick={() => onApply({ actie: "item.remove", id: i.id })}>
                <XIcon width={13} height={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {beschikbaar.length > 0 && (
        <div className="takengroep__toevoegen">
          <select className="fui-field__input" value={toevoegen} aria-label={`Taakregel toevoegen aan ${pkg.name}`}
            onChange={(e) => setToevoegen(e.target.value)}>
            <option value="">Taakregel toevoegen…</option>
            {beschikbaar.map((tpl) => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}
          </select>
          <Button variant="secondary" disabled={!toevoegen}
            onClick={() => onApply({ actie: "item.add", packageId: pkg.id, templateId: toevoegen }, () => setToevoegen(""))}>
            Toevoegen
          </Button>
        </div>
      )}
      {vraag && (
        <ConfirmDialog
          title={vraag === "uit" ? `${pkg.name} uitzetten?` : `${pkg.name} verwijderen?`}
          confirmLabel={vraag === "uit" ? "Uitzetten" : "Verwijderen"}
          cancelLabel="Annuleren"
          danger
          onCancel={() => setVraag(null)}
          onConfirm={() => {
            onApply(vraag === "uit" ? { actie: "package.toggle", id: pkg.id, active: false } : { actie: "package.delete", id: pkg.id });
            setVraag(null);
          }}
        >
          <p>
            Bij het inboeken van {voorWie} komen dan {pkg.items.length === 1 ? "deze taak" : `deze ${pkg.items.length} taken`} niet
            meer automatisch, tenzij een regel ook in een andere actieve groep zit.
            {vraag === "weg" && " De taakregels zelf blijven bestaan."}
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
